import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import MultiSelect from './MultiSelect';
import PeriodPicker from './PeriodPicker';
import { accountsService, agentsService, channelsService, teamsService, exportUrls } from '../services/api';
import { daysAgo, toApiDay, today } from '../utils/datetime';

const STORAGE_KEY = 'dashboard:filters:v2';

const loadStoredSelection = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return {
      accountIds: stored?.accountIds || [],
      teamIds: stored?.teamIds || [],
      agentIds: stored?.agentIds || [],
      inboxIds: stored?.inboxIds || [],
    };
  } catch {
    return { accountIds: [], teamIds: [], agentIds: [], inboxIds: [] };
  }
};

/**
 * Barra de filtros em cascata: conta -> times -> agentes -> canais.
 *
 * A conta é o filtro raiz porque a plataforma atende múltiplas operações e as
 * demais entidades (times, agentes e canais) pertencem a uma conta específica.
 * Ao trocar de conta, seleções que não existem mais no novo escopo são descartadas.
 */
const FilterBar = ({ onFilterChange }) => {
  const stored = useMemo(loadStoredSelection, []);

  const [startDate, setStartDate] = useState(() => daysAgo(7));
  const [endDate, setEndDate] = useState(today);

  const [accountIds, setAccountIds] = useState(stored.accountIds);
  const [teamIds, setTeamIds] = useState(stored.teamIds);
  const [agentIds, setAgentIds] = useState(stored.agentIds);
  const [inboxIds, setInboxIds] = useState(stored.inboxIds);

  const [accounts, setAccounts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [channels, setChannels] = useState([]);

  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingDependents, setLoadingDependents] = useState(false);

  // Chaves estáveis: os arrays são recriados a cada render e não servem como dependência
  const accountKey = accountIds.join(',');
  const teamKey = teamIds.join(',');
  const agentKey = agentIds.join(',');
  const inboxKey = inboxIds.join(',');

  // Contas: carregadas uma única vez, são a raiz da cascata
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const response = await accountsService.getAll();
        setAccounts(response.data.data || []);
      } catch (error) {
        console.error('Error loading accounts:', error);
      } finally {
        setLoadingAccounts(false);
      }
    };

    loadAccounts();
  }, []);

  // Times e canais dependem apenas das contas selecionadas
  useEffect(() => {
    if (!accountKey) {
      setTeams([]);
      setChannels([]);
      setTeamIds([]);
      setInboxIds([]);
      return undefined;
    }

    const selectedAccounts = accountKey.split(',');
    let active = true;

    const loadScoped = async () => {
      setLoadingDependents(true);
      try {
        const [teamsRes, channelsRes] = await Promise.all([
          teamsService.getAll({ accountIds: selectedAccounts }),
          channelsService.getAll({ accountIds: selectedAccounts }),
        ]);

        if (!active) return;

        const nextTeams = teamsRes.data.data || [];
        const nextChannels = channelsRes.data.data || [];

        setTeams(nextTeams);
        setChannels(nextChannels);

        // Descarta seleções que não pertencem mais ao escopo das contas
        const teamValues = new Set(nextTeams.map((team) => String(team.id)));
        const channelValues = new Set(nextChannels.map((channel) => String(channel.id)));
        setTeamIds((current) => current.filter((id) => teamValues.has(id)));
        setInboxIds((current) => current.filter((id) => channelValues.has(id)));
      } catch (error) {
        console.error('Error loading teams/channels:', error);
      } finally {
        if (active) setLoadingDependents(false);
      }
    };

    loadScoped();
    return () => {
      active = false;
    };
  }, [accountKey]);

  // Agentes dependem das contas e, quando houver, dos times selecionados
  useEffect(() => {
    if (!accountKey) {
      setAgents([]);
      setAgentIds([]);
      return undefined;
    }

    let active = true;

    const loadAgents = async () => {
      try {
        const response = await agentsService.getAll({
          accountIds: accountKey.split(','),
          teamIds: teamKey ? teamKey.split(',') : undefined,
        });

        if (!active) return;

        const nextAgents = response.data.data || [];
        setAgents(nextAgents);

        const agentValues = new Set(nextAgents.map((agent) => String(agent.id)));
        setAgentIds((current) => current.filter((id) => agentValues.has(id)));
      } catch (error) {
        console.error('Error loading agents:', error);
      }
    };

    loadAgents();
    return () => {
      active = false;
    };
  }, [accountKey, teamKey]);

  // Propaga os filtros para o Dashboard e persiste a seleção entre sessões.
  // Enquanto o intervalo estiver pela metade (só data inicial), não busca nada.
  useEffect(() => {
    if (!startDate || !endDate) return;

    const selection = {
      accountIds: accountKey ? accountKey.split(',') : [],
      teamIds: teamKey ? teamKey.split(',') : [],
      agentIds: agentKey ? agentKey.split(',') : [],
      inboxIds: inboxKey ? inboxKey.split(',') : [],
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch {
      // localStorage indisponível (modo privado) — filtros seguem funcionando na sessão
    }

    // A API recorta o período por dia do calendário em São Paulo — mandar o dia
    // literal evita que o servidor tenha de reinterpretar um instante UTC.
    onFilterChange({
      startDate: toApiDay(startDate),
      endDate: toApiDay(endDate),
      ...selection,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, accountKey, teamKey, agentKey, inboxKey]);

  const handlePeriodChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const resetFilters = () => {
    setStartDate(daysAgo(7));
    setEndDate(today());
    setAccountIds([]);
    setTeamIds([]);
    setAgentIds([]);
    setInboxIds([]);
  };

  const handleExportExcel = () => {
    window.open(
      exportUrls.conversations({
        startDate: toApiDay(startDate),
        endDate: toApiDay(endDate),
        accountIds,
        teamIds,
        agentIds,
        inboxIds,
      }),
      '_blank'
    );
  };

  const accountOptions = accounts.map((account) => ({
    value: String(account.id),
    label: account.name,
    hint: `${account.total_inboxes} canais · ${account.total_teams} times`,
  }));

  const teamOptions = teams.map((team) => ({
    value: String(team.id),
    label: team.name,
    group: team.account_name,
    hint: `${team.total_members} agentes`,
  }));

  const agentOptions = agents.map((agent) => ({
    value: String(agent.id),
    label: agent.name,
    group: agent.account_names,
    hint: agent.team_names || undefined,
  }));

  const channelOptions = channels.map((channel) => ({
    value: String(channel.id),
    label: channel.name,
    group: channel.account_name,
    hint: channel.channel_type?.replace('Channel::', ''),
  }));

  const hasAccount = accountIds.length > 0;

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex flex-wrap items-start gap-4">
        {/* Período */}
        <PeriodPicker startDate={startDate} endDate={endDate} onChange={handlePeriodChange} />

        {/* Cascata: conta -> times -> agentes -> canais */}
        <MultiSelect
          label="Conta"
          options={accountOptions}
          value={accountIds}
          onChange={setAccountIds}
          loading={loadingAccounts}
          placeholder="Selecione a conta"
        />

        <MultiSelect
          label="Time"
          options={teamOptions}
          value={teamIds}
          onChange={setTeamIds}
          loading={loadingDependents && hasAccount}
          disabled={!hasAccount}
          placeholder="Todos os times"
          emptyMessage={hasAccount ? 'Conta sem times' : 'Selecione a conta'}
          helpText={
            hasAccount && !loadingDependents && teams.length === 0
              ? 'Nenhum time configurado nesta conta'
              : undefined
          }
        />

        <MultiSelect
          label="Agente"
          options={agentOptions}
          value={agentIds}
          onChange={setAgentIds}
          disabled={!hasAccount}
          placeholder="Todos os agentes"
          emptyMessage={hasAccount ? 'Nenhum agente' : 'Selecione a conta'}
        />

        <MultiSelect
          label="Canal"
          options={channelOptions}
          value={inboxIds}
          onChange={setInboxIds}
          loading={loadingDependents && hasAccount}
          disabled={!hasAccount}
          placeholder="Todos os canais"
          emptyMessage={hasAccount ? 'Nenhum canal' : 'Selecione a conta'}
        />

        {/* Ações */}
        <div className="flex gap-2 pt-6">
          <button
            onClick={handleExportExcel}
            disabled={!hasAccount}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Exportar Excel
          </button>

          <button
            onClick={resetFilters}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Limpar
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
